import { Link } from 'react-router-dom';
import { Section } from '@/components/layout';
import { GoldLine, OrnamentIcon, VipCrown } from '@/components/ornaments';
import type { OrnamentIconName } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { Prose } from '@/components/typography';
import { TicketCta } from '@/components/ui';
import { EVENTS_BY_SLUG, formatDateRangeLong, type FaqItem, type TourEvent } from '@/features/events';
import { AssetImage } from './AssetImage';
import styles from './BriefBlocks.module.css';

/* =============================================================================
 * BLOQUES EXIGIDOS POR EL BRIEF (ADR-006)
 * -----------------------------------------------------------------------------
 * El mockup manda en lo VISUAL; el brief manda en el CONTENIDO. Estos bloques no
 * los dibuja ningun PNG, pero los pide el master prompt §7 y
 * `docs/brief/07_ARQUITECTURA_LANDINGS.md` §5-6.
 *
 * Se construyen con el lenguaje visual del mockup — misma display serif, mismas
 * versalitas espaciadas, mismos filetes dorados, mismo aire — y se colocan antes
 * del cierre. Registrados en `docs/VISUAL_DEVIATIONS.md` como ADICION EXIGIDA
 * POR EL BRIEF.
 *
 * ADR-003 SIGUE INTACTO: aqui no se inventa un solo dato. Lo que no existe
 * (precio, sede) se dice que se anunciara, y se marca como pendiente.
 * ========================================================================== */

interface Paso {
  readonly id: string;
  readonly icon: OrnamentIconName;
  readonly title: string;
  readonly text: string;
}

/**
 * "Qué vivirás" — master prompt §7. Los cuatro momentos del recorrido, incluido
 * el **año 2040**, que es el motor de urgencia de toda la campaña y hasta ahora
 * no aparecia en ninguna parte de la web.
 *
 * El contenido sale de `docs/brief/02_BRIEFING_ESTRATEGIA.md` §1: son los bloques
 * reales del programa, no una redaccion nueva.
 */
const PASOS: readonly Paso[] = [
  {
    id: 'desprogramacion',
    icon: 'eye',
    title: 'Desprogramación',
    text: 'Desmantelamos la ingeniería del ego y los bucles de la Matrix: los 1.000 sueños del yo perdido.',
  },
  {
    id: 'anio-2040',
    icon: 'moon',
    title: 'Año 2040',
    text: 'El mapa de la transición: análisis geopolítico-holográfico, historia galáctica y el fin de los tiempos.',
  },
  {
    id: 'camara',
    icon: 'seed',
    title: 'Cámara Cuántica',
    text: 'Sesión grupal en vivo con la Cámara Cuántica Pleyadiana: calibración y salto de frecuencia.',
  },
  {
    id: 'activacion',
    icon: 'star',
    title: 'Activación',
    text: 'Taller de ingeniería mental y sentidos sutiles: telepatía interna, Campo Akáshico y ADN.',
  },
];

export function ExperienceSection() {
  return (
    <Section background="surface" labelledBy="que-viviras-title" width="default">
      <Reveal>
        <div className={styles.heading}>
          <h2 className={styles.title} id="que-viviras-title">
            Qué vivirás
          </h2>
          <GoldLine ornament="diamond" width="sm" />
          <Prose align="center" size="sm" className={styles.intro}>
            Dos días de inmersión con la misma estructura en cada ciudad, y un giro propio en cada sede.
          </Prose>
        </div>
      </Reveal>

      <ul className={styles.steps} role="list">
        {PASOS.map((paso, index) => (
          <Reveal as="li" className={styles.step} delay={index * 0.06} key={paso.id}>
            <span className={styles.stepIcon} aria-hidden="true">
              <OrnamentIcon name={paso.icon} size={44} />
            </span>
            <h3 className={styles.stepTitle}>{paso.title}</h3>
            <p className={styles.stepText}>{paso.text}</p>
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}

/**
 * Prueba social. **La única real que existe** es la foto de la charla de Miami
 * de 2023 ("Semillas Estelares"). ADR-003 §2 y ADR-006 lo dicen expresamente:
 * **sin cifras de asistentes y sin testimonios inventados**. Por eso aquí hay
 * una foto y un pie honesto, y nada más.
 * Desde el 2-nov-2026 hay además testimonios reales en vídeo, solo en el hub
 * (`HubTestimonios`); esta sección no cambia.
 *
 * ── EL TÍTULO, Y POR QUÉ NO ES EL QUE PIDIÓ EL CLIENTE (28-jul-2026) ─────────
 * Pidieron «un título que diga algo como *otros ya han estado en otras giras*,
 * para que la gente pueda ver la foto». La intención es correcta y estaba mal
 * resuelta: el titular decía «Semillas Estelares», que es el NOMBRE del
 * encuentro de 2023 y no dice nada a quien no lo conozca. Ahora el titular es el
 * mensaje y el nombre del encuentro baja al cuerpo, que es su sitio.
 *
 * **Pero no puede decir "en otras giras", porque no las ha habido.** La gira
 * 2027 es la primera; lo que existe es una charla en Miami en 2023, que es
 * justamente la foto. Escribir «otras giras» sería inventar un historial —ADR-003
 * §2, la línea roja de este proyecto— y encima es innecesario: «otros ya han
 * estado antes» dice lo mismo que el cliente quiere decir y es verdad.
 * Avisado al cliente.
 */
export function SocialProofSection() {
  return (
    <Section background="alt" labelledBy="prueba-social-title" width="default">
      <div className={styles.proof}>
        <Reveal className={styles.proofMedia}>
          <AssetImage
            asset="comun/charla-miami-2023"
            className={styles.proofImg}
            sizes="(min-width: 768px) 55vw, 100vw"
          />
        </Reveal>

        <div className={styles.proofCopy}>
          <Reveal>
            <h2 className={styles.title} id="prueba-social-title">
              Otros ya han estado antes
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <GoldLine ornament="star" width="sm" />
          </Reveal>
          <Reveal delay={0.12}>
            <Prose size="sm">
              <p>
                Esta es una fotografía real del encuentro «Semillas Estelares», que Azrahel del Mayor
                reunió en Miami en 2023. La Gira 2027 no empieza de cero: es la continuación de ese
                camino, ahora en cinco ciudades.
              </p>
            </Prose>
          </Reveal>
          <Reveal delay={0.18}>
            <p className={styles.proofCaption}>Charla en Miami, 2023.</p>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

/**
 * Upsell a Cancún: el techo del embudo (`07_ARQUITECTURA_LANDINGS.md` §6b y §7).
 * Se pinta desde el hub y desde cada ciudad.
 *
 * El copy sale del `salesHook` que escribió el propio cliente
 * (`02_BRIEFING_ESTRATEGIA.md` §2), no de una redacción mía. La formulación de
 * la sede es la segura que exige ADR-003 §3: "Cancún / Riviera Maya".
 *
 * ── ENCARGO DEL CLIENTE, 28-jul-2026 (vídeo de Loom) ─────────────────────────
 * Éste es el bloque que señalaron en el minuto 2:14 y del que dijeron que les
 * gustaba y **debía estar en todas**. Hasta hoy vivía sólo en `/buenos-aires` y
 * en el hub: Bogotá, Miami y México tenían su propia versión —una tarjeta
 * centrada, sin foto (`SiguienteNivel*`, `CierreMexico`)—, así que cuatro
 * ciudades enseñaban tres cierres distintos del mismo embudo.
 *
 * Y pidieron dos cosas más, las dos aquí:
 *   · «una parte donde se explique que EL RECORRIDO DE LA GIRA ACABA AQUÍ»
 *     -> `desde`, la línea de continuidad, que nombra la ciudad de origen. Es
 *        lo que convierte cinco páginas sueltas en un recorrido con final.
 *   · «y QUE TAMBIÉN PUEDAN RESERVAR» -> el `TicketCta` de Cancún, junto al
 *     enlace de siempre. Antes sólo se podía "descubrir": para comprar había
 *     que llegar a `/cancun` y buscar el botón. Dos pasos para una intención
 *     que ya estaba tomada.
 */
export function CancunUpsell({ desde }: { desde?: TourEvent } = {}) {
  const cancun = EVENTS_BY_SLUG.cancun;

  return (
    <Section background="surface" labelledBy="upsell-cancun-title" width="default">
      <Reveal>
        <div className={styles.upsell}>
          <div className={styles.upsellCopy}>
            <p className={styles.upsellEyebrow}>
              <VipCrown size={20} />
              El nivel final del recorrido
            </p>
            <h2 className={styles.upsellTitle} id="upsell-cancun-title">
              {cancun.titleMain}
            </h2>
            {/* La continuidad, sólo cuando se viene de una ciudad. En el hub no
                se pinta: allí el visitante no está "en" ningún destino, así que
                un "después de X" no tendría antecedente. */}
            {desde ? (
              <p className={styles.upsellDesde}>
                El recorrido de la gira termina aquí. Después de {desde.shortName}, el último escalón.
              </p>
            ) : null}
            <p className={styles.upsellText}>
              Convivencia VIP en {cancun.city}, {formatDateRangeLong(cancun)}. Petit comité, acceso directo
              a Azrahel y experiencia inmersiva: amanecer, cenote, Cámara Cuántica y fogata ritual.
            </p>
            <div className={styles.upsellAcciones}>
              {/* PRIMERO reservar y después descubrir, y no al revés: quien ya
                  ha leído toda una landing de ciudad y llega hasta aquí no
                  necesita que se le convenza otra vez, necesita poder comprar. */}
              <TicketCta event={cancun} variant="premium" size="md" icon={<VipCrown size={18} />} />
              <Link className={styles.upsellCta} to={cancun.path}>
                Descubrir la convivencia VIP
              </Link>
            </div>
          </div>

          <div className={styles.upsellMedia}>
            <AssetImage
              asset="hub/card-cancun"
              className={styles.upsellImg}
              sizes="(min-width: 768px) 36vw, 100vw"
            />
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/**
 * FAQ. Las preguntas y respuestas salen de `src/features/events` (ADR-003): esta
 * página no redacta ni una. Las que dependen de un dato que el cliente todavía no
 * ha dado vienen con `isPending: true` y se marcan como tal — la respuesta honesta
 * es "se anunciará", nunca un precio o una sede inventados.
 *
 * `<details>/<summary>` nativo: accesible con teclado, sin JavaScript y sin
 * depender de ninguna librería.
 *
 * **PLEGADA, con una sola respuesta abierta a la vez** (patrón de FORGE-ECHO
 * que el ORACLE adopta para las seis páginas, rectificando el "desplegada" que
 * yo tenía). Cumple mejor el mismo criterio: *una objeción que frena una compra
 * no se esconde tras un clic* —la PREGUNTA, que es donde vive la objeción, está
 * siempre visible— y además se puede escanear la lista de un vistazo en vez de
 * ser un muro de texto.
 *
 * `name` compartido = acordeón exclusivo NATIVO, sin una línea de JavaScript.
 * Donde no esté soportado degrada a acordeón normal, nunca a contenido inaccesible.
 *
 * **La marca de pendiente va en el `summary`, no en la respuesta**: si viviera
 * dentro del pliegue, lo que ADR-003 obliga a declarar sólo se vería al abrir.
 * En el hub son 4 de 9 preguntas.
 */
export function FaqSection({
  items,
  titulo = 'Preguntas frecuentes',
  grupo = 'faq',
}: {
  items: readonly FaqItem[];
  titulo?: string;
  /** `name` del acordeón: comparte grupo para que sólo una esté abierta. */
  grupo?: string;
}) {
  if (items.length === 0) return null;

  return (
    <Section background="alt" labelledBy="faq-title" width="default">
      <Reveal>
        <div className={styles.heading}>
          <h2 className={styles.title} id="faq-title">
            {titulo}
          </h2>
          <GoldLine ornament="diamond" width="sm" />
        </div>
      </Reveal>

      <ul className={styles.faq} role="list">
        {items.map((item, index) => (
          <Reveal as="li" className={styles.faqItem} delay={index * 0.05} key={item.id}>
            <details className={styles.faqDetails} name={grupo}>
              <summary className={styles.faqSummary}>
                <span className={styles.faqQuestion}>
                  {item.question}
                  {item.isPending ? <span className={styles.faqPending}>Por anunciar</span> : null}
                </span>
                {/* Ornamento CSS puro: dos filetes de oro cruzados en `+` que al
                    abrir giran y se apagan hasta quedar en `–`. Sin icono ni sprite. */}
                <span className={styles.faqMark} aria-hidden="true" />
              </summary>
              <p className={styles.faqAnswer}>{item.answer}</p>
            </details>
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}
